<template>
    <div>
        <div id="Dashboard" class="container is-fluid">
            <link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet" />

            <!-- Header -->
            <HeroContainer title="Margin Spread Dashboard" />

            <section id="filters" class="filters-section">
                <div class="card shadow-sm filters-card">
                    <div class="card-content">
                        <div class="filters-body">
                            <div class="field">
                                <input
                                    type="text"
                                    class="input"
                                    placeholder="Part (optional)"
                                    v-model="part_number"
                                    @input="part_number = $event.target.value.toUpperCase()"
                                />
                            </div>
                            <div class="field inline-range">
                                <input
                                    type="number"
                                    class="input range-input"
                                    v-model="percent_difference_low"
                                    placeholder="%"
                                />

                                <span class="material-icons">chevron_left</span>
                                <span>Percent Difference</span>
                                <span class="material-icons">chevron_left</span>

                                <input
                                    type="number"
                                    class="input range-input"
                                    v-model="percent_difference_high"
                                    placeholder="%"
                                />
                            </div>
                            <div class="field inline-range">
                                <input
                                    type="number"
                                    class="input range-input"
                                    v-model="price_difference_low"
                                    placeholder="$"
                                />
                                <span class="material-icons">chevron_left</span>
                                <span>Price Difference</span>
                                <span class="material-icons">chevron_left</span>
                                <input
                                    type="number"
                                    class="input range-input"
                                    v-model="price_difference_high"
                                    placeholder="$"
                                />
                            </div>
                            <div class="field has-addons date-row">
                                <p class="control">
                                    <span class="select">
                                        <select
                                            id="date_range"
                                            v-model="date_range"
                                            @change="change_date(date_range)"
                                        >
                                            <option disabled hidden value="" style="color: grey">
                                                Date Presets
                                            </option>
                                            <option value="today">Today</option>
                                            <option value="week">Week</option>
                                            <option value="this_month">This Month</option>
                                            <option value="prev_month">Prev Month</option>
                                            <option value="this_quarter">This Quarter</option>
                                            <option value="prev_quarter">Prev Quarter</option>
                                            <option value="this_year">This Year</option>
                                            <option value="prev_year">Prev Year</option>
                                            <option value="all">All</option>
                                            <option disabled hidden value="custom">Custom</option>
                                        </select>
                                    </span>
                                </p>
                                <p class="control is-expanded">
                                    <VueDatePicker
                                        v-model="range"
                                        :enable-time-picker="false"
                                        range
                                    />
                                </p>
                            </div>
                        </div>
                        <div class="buttons is-right filters-actions">
                            <button @click="reset" class="button is-small is-white">Reset</button>
                            <div class="filter-action">
                                <button
                                    @click="ajax_get_dashboard"
                                    class="button is-info"
                                    :disabled="isSearching"
                                >
                                    Filter
                                </button>
                                <span
                                    v-if="isSearching"
                                    class="loading-icon"
                                    aria-live="polite"
                                    aria-label="Loading margin data"
                                >
                                    <i class="material-icons rotating">.</i>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section id="dashboard_data" v-if="rows.length > 0">
                <div class="title_div">
                    <span class="site">Margin Spread Data</span>
                </div>
                <div class="buttons is-right filters-actions" v-if="marginSelectedIds.length > 0">
                    <button
                        class="button"
                        v-if="is_staff || dashboard_permissions.includes('margin_spread_research')"
                        @click="research_parts"
                    >
                        Purchase Research
                    </button>
                    <button
                        class="button"
                        v-if="is_staff || dashboard_permissions.includes('margin_spread_research')"
                        @click="ajax_no_purchase"
                    >
                        No Buy
                    </button>
                    <button class="button" @click="ajax_dashboard_margin_remove">Remove</button>
                </div>
                <SimpleDataTable
                    ref="marginTable"
                    :headers="columns"
                    :data="rows"
                    row-key="part_number"
                    :default-items-per-page="5"
                    :items-per-page-options="[3, 5, 7, 10, 20, 50]"
                    @selection-changed="onMarginSelection"
                />
                <div class="buttons is-right">
                    <button
                        class="button is-info"
                        style="margin: 0.5em 0 0 0"
                        @click="$emit('download-csv', [rows, 'MarginDashboard', null])"
                    >
                        Download CSV
                    </button>
                </div>
            </section>

            <section id="dashboard_removed_data" v-if="removed_rows.length > 0">
                <div class="title_div">
                    <span class="site">Removed Spread Data</span>
                </div>
                <div class="buttons is-right filters-actions" v-if="removedSelectedIds.length > 0">
                    <button class="button" @click="ajax_dashboard_margin_add">Add</button>
                </div>
                <SimpleDataTable
                    ref="removedTable"
                    :headers="columns"
                    :data="removed_rows"
                    row-key="part_number"
                    :default-items-per-page="5"
                    :items-per-page-options="[3, 5, 7, 10, 20, 50]"
                    @selection-changed="onRemovedSelection"
                />
                <div class="buttons is-right">
                    <button
                        class="button is-info"
                        style="margin: 0.5em 0 0 0"
                        @click="
                            $emit('download-csv', [removed_rows, 'MarginDashboardRemoved', null])
                        "
                    >
                        Download CSV
                    </button>
                </div>
            </section>
        </div>
    </div>
</template>

<style scoped>
    .filters-section {
        display: flex;
        justify-content: center;
    }

    .filters-card {
        width: 100%;
        max-width: 750px;
        margin: 0 auto 1.5em;
    }

    .filters-card .card-content {
        padding: 1.5rem;
    }

    .filters-body {
        display: flex;
        flex-direction: column;
        gap: 1em;
    }

    .inline-range {
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex-wrap: wrap;
        gap: 0.5em;
    }

    .range-input {
        width: 7em;
    }

    .date-row {
        margin-top: 0;
    }

    .filters-actions {
        margin-top: 0.5em;
        gap: 0.75em;
    }

    .filter-action {
        display: flex;
        align-items: center;
        gap: 0.5em;
    }

    .loading-icon {
        display: flex;
        align-items: center;
        color: #3273dc;
    }

    .rotating {
        animation: spin 1s linear infinite;
    }

    @keyframes spin {
        from {
            transform: rotate(0deg);
        }
        to {
            transform: rotate(360deg);
        }
    }

    .title_div {
        margin-top: 1.5em;
    }

    .site {
        font-size: 2em;
    }
    #Dashboard > section {
        margin-bottom: 2em;
        max-width: 100%;
    }
</style>

<script>
    import VueDatePicker from '@vuepic/vue-datepicker'
    import moment from 'moment'
    var numeral = require('numeral')
    import {
        DASHBOARD_MARGIN,
        PURCHASE_RESEARCH_CREATE_MULTIPLE,
        DASHBOARD_MARGIN_REMOVE,
        DASHBOARD_MARGIN_ADD,
    } from '@/data/url'
    import { getDates } from '@/helpers/dates.js'
    import config from '@/services/config'
    import HeroContainer from '@/components/shared/HeroContainer.vue'
    import SimpleDataTable from '@/components/shared/SimpleDataTable.vue'
    import { mapStores } from 'pinia'
    import { useUserStore } from '@/stores/userStore'
    import { useQuoteFormStore } from '@/stores/quoteFormStore'

    export default {
        name: 'DashboardMargin',
        components: {
            VueDatePicker,
            HeroContainer,
            SimpleDataTable,
        },
        computed: {
            ...mapStores(useUserStore, useQuoteFormStore),
            date_range: {
                get: function () {
                    return this.quoteFormStore.formData?.date_range
                },
                set: function (value) {
                    this.quoteFormStore.setFormData({
                        ...this.quoteFormStore.formData,
                        date_range: value,
                    })
                },
            },
            range: {
                get: function () {
                    return this.quoteFormStore.formData?.range
                },
                set: function (value) {
                    this.quoteFormStore.setFormData({
                        ...this.quoteFormStore.formData,
                        range: value,
                    })
                },
            },
            is_staff: function () {
                return this.userStore.is_staff
            },
            dashboard_permissions: function () {
                let dashboard_permissions = this.userStore.dashboard_permissions
                if (dashboard_permissions) {
                    return dashboard_permissions
                }
                return []
            },
            queryData() {
                let raw_query = [...this.queryDataRaw]
                return raw_query
            },
            rows() {
                let data = this.queryData
                let keep_rows = []
                for (let d of data) {
                    if (d.removed != true) {
                        keep_rows.push(d)
                    }
                }
                return keep_rows
            },
            removed_rows() {
                let data = this.queryData
                let removed_rows = []
                for (let d of data) {
                    if (d.removed == true) {
                        removed_rows.push(d)
                    }
                }
                return removed_rows
            },
            is_paged: function () {
                if (this.queryData.length <= 10) {
                    return false
                } else {
                    return true
                }
            },
            isSearching() {
                return this.searchStore.ajax_searching > 0
            },
            marginSelectedRows() {
                return this.rows.filter((row) => this.marginSelectedIds.includes(row.part_number))
            },
            removedSelectedRows() {
                return this.removed_rows.filter((row) =>
                    this.removedSelectedIds.includes(row.part_number)
                )
            },
            columns: function () {
                return [
                    {
                        label: 'Time Created',
                        field: 'time_created',
                        type: 'text',
                    },

                    {
                        label: 'Part Number',
                        field: 'part_number',
                        type: 'text',
                    },
                    {
                        label: 'Description',
                        field: 'description',
                        type: 'text',
                    },
                    {
                        label: 'Greenwood Ponca Inventory',
                        field: 'inventory_component',
                        type: 'number',
                    },
                    {
                        label: 'Last Sold',
                        field: 'last_sold',
                        type: 'text',
                    },
                    {
                        label: 'Sales  12 Mo',
                        field: 'rolling_year_sales',
                        type: 'number',
                    },
                    {
                        label: 'Quotes 12 Mo',
                        field: 'rolling_year_quotes',
                        type: 'number',
                    },
                    {
                        label: 'Textron Price',
                        field: 'textron_price',
                        type: 'number',
                        formatFn: function (value) {
                            return numeral(value).format('$0,0,0,0,0.00')
                        },
                    },
                    {
                        label: 'Textron Stock',
                        field: 'textron_stock',
                        type: 'number',
                        formatFn: function (value) {
                            return numeral(value).format('0,0,0,0,0')
                        },
                    },
                    {
                        label: 'Textron Defense Price',
                        field: 'textron_defense_price',
                        type: 'number',
                        formatFn: function (value) {
                            return numeral(value).format('$0,0,0,0,0.00')
                        },
                    },
                    {
                        label: 'Textron Defense Stock',
                        field: 'textron_defense_stock',
                        type: 'number',
                        formatFn: function (value) {
                            return numeral(value).format('0,0,0,0,0')
                        },
                    },
                    {
                        label: 'Price Diff',
                        field: 'price_difference',
                        type: 'text',
                        formatFn: function (value) {
                            return numeral(value).format('$0,0,0,0,0.00')
                        },
                    },
                    {
                        label: 'Percent Diff',
                        field: 'percent_difference',
                        type: 'number',
                        formatFn: function (value) {
                            return numeral(value).format('0,0,0,0,0.00') + '%'
                        },
                    },
                    {
                        label: 'Avg Price',
                        field: 'abg_price',
                        type: 'number',
                        formatFn: function (value) {
                            return numeral(value).format('$0,0,0,0,0.00')
                        },
                    },
                    {
                        label: 'Research',
                        field: 'research',
                        type: 'boolean',
                    },
                    {
                        label: 'Last Research',
                        field: 'last_research',
                        type: 'text',
                        formatFn: function (value) {
                            if (value == null) {
                                return value
                            }
                            return value.split('T')[0]
                        },
                    },
                    {
                        label: 'PO Ordered',
                        field: 'po_qty_ordered',
                        type: 'number',
                    },
                    {
                        label: 'PO Backorder',
                        field: 'po_qty_back_order',
                        type: 'number',
                    },
                    {
                        label: 'PO Next Delivery',
                        field: 'po_next_delivery_date',
                        type: 'text',
                    },
                ]
            },
        },
        data: function () {
            return {
                part_number: '',
                price_difference_low: '',
                price_difference_high: '',
                percent_difference_low: '',
                percent_difference_high: '',
                queryDataRaw: [],
                displayQuoteButton: false,
                is_bpa_noquote: false,
                marginSelectedIds: [],
                removedSelectedIds: [],
            }
        },
        mounted: function () {
            this.ajax_get_dashboard()
        },
        methods: {
            getDates,

            authHeaders() {
                const headers = { 'Content-Type': 'application/json' }
                const sessionToken = config.sessionToken
                if (sessionToken) headers.Authorization = `Bearer ${sessionToken}`
                return headers
            },
            reset() {
                this.queryDataRaw = []
                this.part_number = ''
                this.price_difference_low = ''
                this.price_difference_high = ''
                this.percent_difference_low = ''
                this.percent_difference_high = ''
                this.date_range = 'week'
                this.range = [moment().subtract(1, 'weeks').toDate(), moment().toDate()]
                this.clearMarginSelection()
                this.clearRemovedSelection()
            },
            rowSelectionChanged() {},
            change_date(date_range) {
                let range = this.getDates(date_range)
                if (range) {
                    this.range = range
                }
            },
            set_searching(is_searching) {
                this.searchStore.set_ajax_searching(is_searching)
            },
            filter_records() {
                this.ajax_get_dashboard(this.filter_customer, this.filter_user)
            },
            research_parts() {
                let part_rows = this.marginSelectedRows
                let parts_list = []
                for (let row of part_rows) {
                    parts_list.push(row.part_number)
                }
                this.$router.push({
                    name: 'home',
                    params: { route_research_parts_list: parts_list },
                })
                this.clearMarginSelection()
            },
            ajax_get_dashboard() {
                this.clearMarginSelection()
                this.clearRemovedSelection()
                this.queryDataRaw = []
                const url = `${config.backendURL || ''}${DASHBOARD_MARGIN}`
                let time_created_begin = moment.utc(this.range[0]).format('YYYY-MM-DD')
                let time_created_end = moment.utc(this.range[1]).add(1, 'day').format('YYYY-MM-DD')
                this.set_searching(true)
                let this_vue = this
                let part_number = this.part_number.trim()
                function set_dash(this_vue, dashboard) {
                    this_vue.queryDataRaw = dashboard
                }
                fetch(url, {
                    method: 'POST',
                    headers: this.authHeaders(),
                    body: JSON.stringify({
                        time_created_begin: time_created_begin,
                        time_created_end: time_created_end,
                        part_number: part_number,
                        percent_difference_low: this.percent_difference_low,
                        percent_difference_high: this.percent_difference_high,
                        price_difference_low: this.price_difference_low,
                        price_difference_high: this.price_difference_high,
                    }),
                })
                    .then((res) => res.json())
                    .then((res) => {
                        this_vue.set_searching(false)
                        if (res.success) {
                            set_dash(this_vue, res.dashboard)
                        }
                    })
                    .catch((e) => {
                        this_vue.set_searching(false)
                        console.error(JSON.stringify(e.message))
                    })
            },
            ajax_no_purchase() {
                let queryDataRaw = this.queryDataRaw
                let part_rows = this.marginSelectedRows
                let parts_dict_list = []
                for (let row of part_rows) {
                    if (row.research == false) {
                        parts_dict_list.push({ part_number: row.part_number, no_purchase: true })
                    }
                }
                if (parts_dict_list.length == 0) {
                    alert('Please select parts that have not already been researched.')
                    return
                }
                this.set_searching(true)
                let this_vue = this
                const url = `${config.backendURL || ''}${PURCHASE_RESEARCH_CREATE_MULTIPLE}`

                function set_researched(part_data) {
                    for (let pd of part_data) {
                        for (let qd of queryDataRaw) {
                            if (pd.part_number == qd.part_number) {
                                qd.research = true
                                qd.last_research = moment().toISOString().split('T')[0]
                                break
                            }
                        }
                    }
                    this_vue.clearMarginSelection()
                }
                function purchase_fail() {
                    alert('Error saving data')
                }
                fetch(url, {
                    method: 'POST',
                    headers: this.authHeaders(),
                    body: JSON.stringify(parts_dict_list),
                })
                    .then((res) => res.json())
                    .then((res) => {
                        this_vue.set_searching(false)
                        if (res.success == true) {
                            let purchase_data = res.data
                            set_researched(purchase_data)
                        } else {
                            alert('Error: Upload error')
                        }
                    })
                    .catch((e) => {
                        console.error(JSON.stringify(e.message))
                        this_vue.set_searching(false)
                        purchase_fail()
                    })
            },
            ajax_dashboard_margin_remove() {
                const url = `${config.backendURL || ''}${DASHBOARD_MARGIN_REMOVE}`
                this.set_searching(true)
                let rows = this.marginSelectedRows
                let part_numbers = rows.map((row) => row.part_number)
                let this_vue = this
                function set_parts(this_vue, part_numbers) {
                    for (let row of this_vue.queryData) {
                        if (part_numbers.includes(row.part_number)) {
                            row.removed = true
                        }
                    }
                }
                fetch(url, {
                    method: 'POST',
                    headers: this.authHeaders(),
                    body: JSON.stringify({ part_numbers: part_numbers }),
                })
                    .then((res) => res.json())
                    .then((res) => {
                        this_vue.set_searching(false)
                        if (res.success) {
                            set_parts(this_vue, res.part_numbers)
                        }
                    })
                    .catch((e) => {
                        this_vue.set_searching(false)
                        console.error(JSON.stringify(e.message))
                    })
                    .finally(() => {
                        this.clearMarginSelection()
                    })
            },
            ajax_dashboard_margin_add() {
                const url = `${config.backendURL || ''}${DASHBOARD_MARGIN_ADD}`
                this.set_searching(true)
                let rows = this.removedSelectedRows
                let part_numbers = rows.map((row) => row.part_number)
                let this_vue = this
                function set_parts(this_vue, part_numbers) {
                    for (let row of this_vue.queryData) {
                        if (part_numbers.includes(row.part_number)) {
                            row.removed = false
                        }
                    }
                }
                fetch(url, {
                    method: 'POST',
                    headers: this.authHeaders(),
                    body: JSON.stringify({ part_numbers: part_numbers }),
                })
                    .then((res) => res.json())
                    .then((res) => {
                        this_vue.set_searching(false)
                        if (res.success) {
                            set_parts(this_vue, res.part_numbers)
                        }
                    })
                    .catch((e) => {
                        console.log('error', e)
                        this_vue.set_searching(false)
                    })
                    .finally(() => {
                        this.clearRemovedSelection()
                    })
            },
            onMarginSelection(selectedIds) {
                this.marginSelectedIds = selectedIds
            },
            onRemovedSelection(selectedIds) {
                this.removedSelectedIds = selectedIds
            },
            clearMarginSelection() {
                this.marginSelectedIds = []
                this.$refs.marginTable?.clearSelection()
            },
            clearRemovedSelection() {
                this.removedSelectedIds = []
                this.$refs.removedTable?.clearSelection()
            },
        },
    }
</script>
